import React, { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { CONTACT, BRAND } from "../lib/data";
import { Instagram, Mail, Send, MapPin } from "lucide-react";

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] },
};

const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", subject: "General", message: "" });
  const [sent, setSent] = useState(false);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  const onSubmit = (e) => {
    e.preventDefault();
    toast.success("Thanks — we'll be in touch soon.");
    setSent(true);
  };

  return (
    <div data-testid="contact-page">
      <section className="px-6 md:px-12 lg:px-16 pt-24 md:pt-32 pb-16">
        <motion.div {...fadeUp} className="max-w-5xl">
          <p className="tracking-overline text-xs text-[#9A7456] mb-6">
            {CONTACT.eyebrow}
          </p>
          <h1
            className="font-display text-5xl md:text-7xl lg:text-8xl leading-[0.98]"
            data-testid="contact-title"
          >
            {CONTACT.headline}
          </h1>
          <p className="font-body text-lg text-[#231F1E]/75 mt-8 max-w-2xl leading-relaxed">
            {CONTACT.intro}
          </p>
        </motion.div>
      </section>

      <section className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 px-6 md:px-12 lg:px-16 pb-24">
        <div className="lg:col-span-5 space-y-12">
          <div>
            <p className="tracking-overline text-xs text-[#9A7456] mb-6">Inbox</p>
            <ul className="space-y-5">
              {CONTACT.channels.map((c) => (
                <li key={c.label} className="flex flex-col">
                  <span className="text-xs tracking-overline text-[#231F1E]/50">
                    {c.label}
                  </span>
                  <a
                    href={c.href}
                    className="font-display text-2xl md:text-3xl text-[#231F1E] link-underline self-start"
                    data-testid={`contact-channel-${c.label.toLowerCase()}`}
                  >
                    {c.value}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <p className="tracking-overline text-xs text-[#9A7456] mb-6">Find us</p>
            <p className="font-body text-[#231F1E] flex items-center gap-3 text-base">
              <MapPin size={16} className="text-[#9A7456]" />
              {BRAND.location}
            </p>
            <div className="mt-6 flex gap-3">
              <a
                href={BRAND.socials.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="w-11 h-11 rounded-full border border-[#231F1E]/25 flex items-center justify-center hover:bg-[#231F1E] hover:text-[#EDE6DA] transition-all"
                data-testid="contact-instagram"
                aria-label="Instagram"
              >
                <Instagram size={16} />
              </a>
              <a
                href={BRAND.socials.email}
                className="w-11 h-11 rounded-full border border-[#231F1E]/25 flex items-center justify-center hover:bg-[#231F1E] hover:text-[#EDE6DA] transition-all"
                data-testid="contact-mail"
                aria-label="Email"
              >
                <Mail size={16} />
              </a>
            </div>
          </div>
        </div>

        <form
          className="lg:col-span-7 space-y-6"
          onSubmit={onSubmit}
          data-testid="contact-form"
        >
          <Field
            label="Name *"
            name="name"
            value={form.name}
            onChange={onChange}
            required
            testid="contact-input-name"
          />
          <Field
            label="Email *"
            name="email"
            type="email"
            value={form.email}
            onChange={onChange}
            required
            testid="contact-input-email"
          />
          <div>
            <label className="block tracking-overline text-[10px] text-[#231F1E]/60 mb-2">
              Subject *
            </label>
            <select
              name="subject"
              value={form.subject}
              onChange={onChange}
              data-testid="contact-input-subject"
              className="w-full bg-transparent border-b border-[#231F1E]/25 py-3 text-[#231F1E] font-body focus:border-[#9A7456] focus:outline-none transition-colors"
            >
              <option>General</option>
              <option>Vendor inquiry</option>
              <option>Partnership / Venue</option>
              <option>Press</option>
            </select>
          </div>
          <div>
            <label className="block tracking-overline text-[10px] text-[#231F1E]/60 mb-2">
              Message *
            </label>
            <textarea
              name="message"
              value={form.message}
              onChange={onChange}
              required
              rows={6}
              data-testid="contact-input-message"
              className="w-full bg-transparent border-b border-[#231F1E]/25 py-3 text-[#231F1E] font-body focus:border-[#9A7456] focus:outline-none transition-colors resize-none"
            />
          </div>
          <button
            type="submit"
            disabled={sent}
            data-testid="contact-submit-button"
            className="btn-primary disabled:opacity-60"
          >
            {sent ? "Sent ✓" : (<>Send message <Send size={14} /></>)}
          </button>
        </form>
      </section>
    </div>
  );
};

const Field = ({ label, name, value, onChange, type = "text", required, testid }) => (
  <div>
    <label className="block tracking-overline text-[10px] text-[#231F1E]/60 mb-2">
      {label}
    </label>
    <input
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      required={required}
      data-testid={testid}
      className="w-full bg-transparent border-b border-[#231F1E]/25 py-3 text-[#231F1E] font-body focus:border-[#9A7456] focus:outline-none transition-colors"
    />
  </div>
);

export default Contact;
